import { useState } from "react";
import { Crosshair, Eye, EyeOff, Link2, Trash2, X } from "lucide-react";
import { useSvgDiagram2D } from "./SvgDiagram2DContext";
import { NODE_SHAPES } from "../../utils/svgDiagram2DModel";
import type { NodeShape } from "../../utils/svgDiagram2DModel";
import { DebouncedInput } from "../reusable/DebouncedInput";
import { CheckboxField } from "../reusable/CheckboxField";
import { SvgDiagram2DNodeSpawnSection } from "./SvgDiagram2DNodeSpawnSection";
import { isMultiNodeSelection } from "./SvgDiagram2DMultiNodePanel";

function num(v: string | number, fallback: number): number {
    const n = typeof v === "number" ? v : Number(v);
    return Number.isFinite(n) ? n : fallback;
}

const SHAPE_LABELS: Record<NodeShape, string> = {
    circle: "Circle",
    square: "Square",
    diamond: "Diamond",
    triangle: "Triangle",
};

/**
 * Floating inspector for the first selected node: edits its intrinsic
 * properties and lets you manage its connections and group membership in one
 * place. Rendered as an overlay inside the viewer; closes itself when no node
 * is selected.
 */
export function SvgDiagram2DNodePanel() {
    const {
        doc,
        selectedNodeIds,
        nodePanelOpen,
        setNodePanelOpen,
        updateNode,
        removeNode,
        pushSnapshot,
        addConnection,
        updateConnection,
        removeConnection,
        toggleNodeEffect,
        updateGroup,
        activePresetId,
        setSelectedNodeId,
    } = useSvgDiagram2D();

    const [connectTarget, setConnectTarget] = useState("");

    const node = doc.nodes.find((n) => n.id === selectedNodeIds[0]);
    if (!nodePanelOpen || !node) return null;
    // 2+ nodes selected → the multi-node panel takes over, so suppress this
    // single-node inspector (mirrors the per-shape panels vs. multi-shape).
    if (isMultiNodeSelection({ selectedNodeIds })) return null;

    const isSpawnClone = Boolean(node.spawnedFrom);
    // The host node this clone's pattern radiates around (the surrounded node).
    const hostNode = node.spawnedFrom
        ? doc.nodes.find((n) => n.id === node.spawnedFrom!.hostId)
        : undefined;
    // The template node this clone is a copy of (its appearance base).
    const templateNode = node.spawnedFrom
        ? doc.nodes.find((n) => n.id === node.spawnedFrom!.templateId)
        : undefined;
    const connections = doc.connections.filter(
        (c) => c.sourceId === node.id || c.targetId === node.id,
    );
    const otherNodes = doc.nodes.filter(
        (n) => n.id !== node.id && !n.spawnedFrom,
    );

    return (
        <div className="diagram-node-panel">
            <div
                className="field-row"
                style={{ alignItems: "center", gap: 6 }}
            >
                <strong style={{ flex: 1, minWidth: 0 }}>Node properties</strong>
                <button
                    type="button"
                    className="generic-button"
                    onClick={() => setNodePanelOpen(false)}
                    title="Close panel"
                    style={{ padding: "2px 6px" }}
                >
                    <X size={14} aria-hidden />
                </button>
            </div>

            {isSpawnClone && (
                <div className="diagram-node-panel-section">
                    <p
                        className="path-tool-meta"
                        style={{ margin: "0 0 6px", opacity: 0.7 }}
                    >
                        Spawn clone — position follows the host, appearance
                        follows the template.
                    </p>
                    <div className="field-row" style={{ gap: 6 }}>
                        <button
                            type="button"
                            className="generic-button"
                            disabled={!hostNode}
                            onClick={() => {
                                if (hostNode) setSelectedNodeId(hostNode.id);
                            }}
                            title={
                                hostNode
                                    ? `Select the host node this clone surrounds (${hostNode.label})`
                                    : "Host node not found"
                            }
                        >
                            <Crosshair size={14} aria-hidden /> Select host
                        </button>
                        <button
                            type="button"
                            className="generic-button"
                            disabled={!templateNode}
                            onClick={() => {
                                if (templateNode)
                                    setSelectedNodeId(templateNode.id);
                            }}
                            title={
                                templateNode
                                    ? `Select the template node this clone is based on (${templateNode.label})`
                                    : "Template node not found"
                            }
                        >
                            <Crosshair size={14} aria-hidden /> Select template
                        </button>
                    </div>
                </div>
            )}

            {/* --- Properties --- */}
            <div className="diagram-node-panel-section">
                <p className="diagram-node-panel-section-title">Properties</p>
                <label className="field-label">
                    Label
                    <input
                        type="text"
                        value={node.label}
                        onChange={(e) => updateNode(node.id, { label: e.target.value })}
                        disabled={isSpawnClone}
                    />
                </label>
                <div className="field-row field-row--two">
                    <label className="field-label">
                        Shape
                        <select
                            className="anchor-select"
                            value={node.shape}
                            onChange={(e) =>
                                updateNode(node.id, {
                                    shape: e.target.value as NodeShape,
                                })
                            }
                            disabled={isSpawnClone}
                        >
                            {NODE_SHAPES.map((s) => (
                                <option key={s} value={s}>
                                    {SHAPE_LABELS[s]}
                                </option>
                            ))}
                        </select>
                    </label>
                    <label className="field-label">
                        Size
                        <DebouncedInput
                            type="number"
                            min={1}
                            step={1}
                            value={node.radius}
                            onChange={(v) =>
                                updateNode(node.id, {
                                    radius: Math.max(1, num(v, 10)),
                                })
                            }
                            disabled={isSpawnClone}
                        />
                    </label>
                </div>
                <div className="field-row field-row--two">
                    <label className="field-label">
                        Color
                        <input
                            type="color"
                            value={node.color}
                            onChange={(e) =>
                                updateNode(node.id, { color: e.target.value })
                            }
                            disabled={isSpawnClone}
                        />
                    </label>
                    <CheckboxField
                        checked={node.visible}
                        onChange={(checked) =>
                            updateNode(node.id, { visible: checked })
                        }
                        disabled={isSpawnClone}
                        label={
                            <span
                                style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 4,
                                }}
                            >
                                {node.visible ? (
                                    <Eye size={14} aria-hidden />
                                ) : (
                                    <EyeOff size={14} aria-hidden />
                                )}
                                Visible
                            </span>
                        }
                        inline
                    />
                </div>
                <CheckboxField
                    checked={node.heatReactive ?? false}
                    onChange={(checked) =>
                        updateNode(node.id, { heatReactive: checked })
                    }
                    disabled={isSpawnClone}
                    label="React to heat zones (shift fill color by proximity)"
                />
                <div className="field-row field-row--two">
                    {(["x", "y"] as const).map((axis) => (
                        <label key={axis} className="field-label">
                            {axis.toUpperCase()}
                            <DebouncedInput
                                type="number"
                                step={1}
                                value={node.position[axis]}
                                disabled={isSpawnClone}
                                onChange={(v) => {
                                    pushSnapshot();
                                    updateNode(node.id, {
                                        position: {
                                            ...node.position,
                                            [axis]: num(v, node.position[axis]),
                                        },
                                    });
                                }}
                            />
                        </label>
                    ))}
                </div>
            </div>

            {/* --- Connections --- */}
            <div className="diagram-node-panel-section">
                <p className="diagram-node-panel-section-title">
                    Connections ({connections.length})
                </p>
                {connections.length === 0 && (
                    <p className="path-tool-meta" style={{ margin: "0 0 6px" }}>
                        No connections yet.
                    </p>
                )}
                {connections.map((conn) => {
                    const otherId =
                        conn.sourceId === node.id ? conn.targetId : conn.sourceId;
                    const other = doc.nodes.find((n) => n.id === otherId);
                    const connPreset = doc.presets.find(
                        (p) => p.id === conn.presetId,
                    );
                    const isArc = connPreset?.routing === "arc";
                    const presetSweepPct = Math.round(
                        (connPreset?.arcSweep ?? 0.5) * 100,
                    );
                    return (
                        <div
                            key={conn.id}
                            style={{ marginBottom: 4 }}
                        >
                            <div
                                className="field-row"
                                style={{ gap: 4, alignItems: "center" }}
                            >
                                <span
                                    className="path-tool-meta"
                                    style={{
                                        flex: 1,
                                        minWidth: 0,
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    {other?.label ?? "—"}
                                </span>
                                <select
                                    className="anchor-select"
                                    value={conn.presetId}
                                    onChange={(e) =>
                                        updateConnection(conn.id, {
                                            presetId: e.target.value,
                                        })
                                    }
                                    style={{ width: 110 }}
                                >
                                    {doc.presets.map((p) => (
                                        <option key={p.id} value={p.id}>
                                            {p.name}
                                        </option>
                                    ))}
                                </select>
                                <button
                                    type="button"
                                    className="generic-button"
                                    onClick={() => removeConnection(conn.id)}
                                    title="Remove connection"
                                    style={{ padding: "2px 6px" }}
                                >
                                    <Trash2 size={14} aria-hidden />
                                </button>
                            </div>
                            {isArc && (
                                <div
                                    className="field-row"
                                    style={{
                                        gap: 4,
                                        alignItems: "center",
                                        marginTop: 4,
                                        paddingLeft: 8,
                                    }}
                                >
                                    <span
                                        className="path-tool-meta"
                                        style={{ flexShrink: 0 }}
                                    >
                                        Arc
                                    </span>
                                    <select
                                        className="anchor-select"
                                        title="Arc flip side"
                                        value={
                                            conn.arcFlip === undefined
                                                ? "default"
                                                : conn.arcFlip
                                                  ? "flipped"
                                                  : "normal"
                                        }
                                        onChange={(e) =>
                                            updateConnection(conn.id, {
                                                arcFlip:
                                                    e.target.value === "default"
                                                        ? undefined
                                                        : e.target.value ===
                                                          "flipped",
                                            })
                                        }
                                        style={{ width: 84 }}
                                    >
                                        <option value="default">Default</option>
                                        <option value="normal">Normal</option>
                                        <option value="flipped">Flipped</option>
                                    </select>
                                    <DebouncedInput
                                        type="number"
                                        min={1}
                                        max={99}
                                        step={1}
                                        title={`Arc depth for this connection (sweep %). Empty follows the preset (${presetSweepPct}%).`}
                                        placeholder={String(presetSweepPct)}
                                        value={
                                            conn.arcSweep === undefined
                                                ? ""
                                                : Math.round(conn.arcSweep * 100)
                                        }
                                        onChange={(v) =>
                                            updateConnection(conn.id, {
                                                // DebouncedInput emits 0 on an
                                                // empty number field; treat
                                                // anything < 1% as "clear the
                                                // override" so emptying the box
                                                // falls back to the preset (the
                                                // × button does the same).
                                                arcSweep:
                                                    num(v, presetSweepPct) < 1
                                                        ? undefined
                                                        : Math.max(
                                                              0.01,
                                                              Math.min(
                                                                  0.99,
                                                                  num(
                                                                      v,
                                                                      presetSweepPct,
                                                                  ) / 100,
                                                              ),
                                                          ),
                                            })
                                        }
                                        style={{ width: 56 }}
                                    />
                                    {conn.arcSweep !== undefined && (
                                        <button
                                            type="button"
                                            className="generic-button"
                                            onClick={() =>
                                                updateConnection(conn.id, {
                                                    arcSweep: undefined,
                                                })
                                            }
                                            title="Reset sweep to preset default"
                                            style={{ padding: "2px 6px" }}
                                        >
                                            <X size={14} aria-hidden />
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
                {!isSpawnClone && (
                    <div
                        className="field-row"
                        style={{ gap: 4, alignItems: "center", marginTop: 6 }}
                    >
                        <select
                            className="anchor-select"
                            value={connectTarget}
                            onChange={(e) => setConnectTarget(e.target.value)}
                            style={{ flex: 1, minWidth: 0 }}
                        >
                            <option value="">Connect to…</option>
                            {otherNodes.map((n) => (
                                <option key={n.id} value={n.id}>
                                    {n.label}
                                </option>
                            ))}
                        </select>
                        <button
                            type="button"
                            className="generic-button"
                            disabled={!connectTarget || !activePresetId}
                            onClick={() => {
                                if (connectTarget && activePresetId) {
                                    addConnection(
                                        node.id,
                                        connectTarget,
                                        activePresetId,
                                    );
                                    setConnectTarget("");
                                }
                            }}
                            title="Add connection (uses active preset)"
                            style={{ padding: "2px 6px" }}
                        >
                            <Link2 size={14} aria-hidden />
                        </button>
                    </div>
                )}
            </div>

            {/* --- Groups --- */}
            <div className="diagram-node-panel-section">
                <p className="diagram-node-panel-section-title">Groups</p>
                {doc.groups.length === 0 ? (
                    <p className="path-tool-meta" style={{ margin: 0 }}>
                        No groups defined.
                    </p>
                ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                        {doc.groups.map((g) => (
                            <CheckboxField
                                key={g.id}
                                checked={g.nodeIds.includes(node.id)}
                                onChange={(checked) => {
                                    const nodeIds = checked
                                        ? [...new Set([...g.nodeIds, node.id])]
                                        : g.nodeIds.filter((id) => id !== node.id);
                                    updateGroup(g.id, { nodeIds });
                                }}
                                label={g.name}
                                inline
                            />
                        ))}
                    </div>
                )}
            </div>

            {/* --- Effects --- */}
            {!isSpawnClone && (
                <div className="diagram-node-panel-section">
                    <p className="diagram-node-panel-section-title">Effects</p>
                    {doc.effects.length === 0 ? (
                        <p className="path-tool-meta" style={{ margin: 0 }}>
                            No effects defined. Add one in the Effects section.
                        </p>
                    ) : (
                        <div
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 4,
                            }}
                        >
                            {doc.effects.map((eff) => (
                                <CheckboxField
                                    key={eff.id}
                                    checked={node.effectIds.includes(eff.id)}
                                    onChange={(checked) =>
                                        toggleNodeEffect(node.id, eff.id, checked)
                                    }
                                    label={eff.name}
                                    inline
                                />
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* --- Spawn --- */}
            {!isSpawnClone && (
                <div className="diagram-node-panel-section">
                    <p className="diagram-node-panel-section-title">Spawn</p>
                    <SvgDiagram2DNodeSpawnSection node={node} />
                </div>
            )}

            {/* --- Danger --- */}
            <div className="diagram-node-panel-section">
                <button
                    type="button"
                    className="generic-button"
                    onClick={() => {
                        removeNode(node.id);
                        setNodePanelOpen(false);
                    }}
                    title="Delete this node"
                >
                    <Trash2 size={14} aria-hidden /> Delete node
                </button>
            </div>
        </div>
    );
}
